import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Minus, Plus, Check, Truck, Shield, RefreshCw, Star, ChevronRight, Package, ShoppingCart, FileText, Download, Clock, Bell, Mail, Share2, Printer, Heart, MapPin, Eye, TrendingUp, AlertTriangle, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import axios from 'axios';
import StockAlert from '../components/StockAlert';
import ProductConfigurator from '../components/ProductConfigurator';
import ProductReviews from '../components/ProductReviews';
import ProductImageCarousel from '../components/ProductImageCarousel';
import ShippingEstimator from '../components/ShippingEstimator';
import WarrantySection from '../components/WarrantySection';
import RequestQuotePopup from '../components/RequestQuotePopup';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { calculateSalePrice } from '../utils/calculateSalePrice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const { addItem, total: cartTotal, itemCount: cartItemCount } = useCart();
  const { formatPrice, convertPrice, currency } = useCurrency();
  const [configuredPrice, setConfiguredPrice] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({}); // { addonId: { selected: bool, quantity: number } }
  const [emailForNotify, setEmailForNotify] = useState('');
  const [nameForNotify, setNameForNotify] = useState('');
  const [phoneForNotify, setPhoneForNotify] = useState('');
  const [cityForNotify, setCityForNotify] = useState('');
  const [provinceForNotify, setProvinceForNotify] = useState('');
  const [notifySubmitted, setNotifySubmitted] = useState(false);
  const [preorderSubmitted, setPreorderSubmitted] = useState(false);
  const [showQuotePopup, setShowQuotePopup] = useState(false);

  // Wishlist state
  const [wishlist, setWishlist] = useState(() => {
    const saved = localStorage.getItem('wishlist');
    return saved ? JSON.parse(saved) : [];
  });
  const isInWishlist = product ? wishlist.some(item => item.id === product.id) : false;

  // Low stock threshold
  const LOW_STOCK_THRESHOLD = 10;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/products/${id}`);
        setProduct(res.data);
        
        // Fetch related products
        const allProducts = await axios.get(`${API_URL}/api/products`);
        const related = allProducts.data.filter(p => 
          p.id !== id && p.category === res.data.category
        ).slice(0, 3);
        setRelatedProducts(related);
      } catch (e) {
        console.error('Error fetching product:', e);
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleConfigChange = (priceData) => {
    setConfiguredPrice(priceData);
  };

  // Calculate total price including add-ons with quantities and currency support
  const calculateTotalPrice = () => {
    // Get the effective price (sale price or regular price)
    const saleInfo = calculateSalePrice(product, currency);
    const isCharmPricing = product?.sale_type === 'regional';
    
    // Map currency to region code (admin uses US/CA, display uses USD/CAD)
    const regionCodeMap = { 'USD': 'US', 'CAD': 'CA', 'EUR': 'EUR' };
    const regionCode = regionCodeMap[currency] || currency;
    
    let basePrice;
    if (saleInfo.isOnSale) {
      // For charm pricing, use the regional sale price directly
      if (isCharmPricing && product?.regional_sale_prices?.[currency]) {
        basePrice = product.regional_sale_prices[currency];
      } else {
        basePrice = saleInfo.effectivePrice;
      }
    } else {
      // Not on sale - use region-specific price
      basePrice = product?.region_prices?.[regionCode] || product?.region_prices?.[currency] || product?.price || 0;
    }
    
    // Add ALL selected add-ons/upgrades with quantities
    if (product?.addons) {
      product.addons.forEach(addon => {
        if (selectedAddons[addon.id]?.selected) {
          const addonPrice = currency === 'CAD' ? (addon.price_cad || addon.price) : addon.price;
          const addonQty = selectedAddons[addon.id]?.quantity || 1;
          basePrice += (addonPrice * addonQty);
        }
      });
    }
    
    return basePrice;
  };

  const handleAddToCart = () => {
    if (product) {
      // Build selected add-ons list with quantities
      const selectedAddonsList = [];
      
      // Add ALL selected add-ons/upgrades with quantities
      // The UI now displays all addons as checkboxes, so we just check if they're selected
      if (product.addons) {
        product.addons.forEach(addon => {
          // Check if this addon is selected in the selectedAddons state
          if (selectedAddons[addon.id]?.selected) {
            selectedAddonsList.push({ 
              ...addon, 
              quantity: selectedAddons[addon.id]?.quantity || 1,
              price: addon.price,
              price_cad: addon.price_cad || addon.price
            });
          }
        });
      }
      
      // Calculate the effective price (sale or regular) for the cart
      const saleInfo = calculateSalePrice(product, currency);
      const effectivePrice = saleInfo.isOnSale ? saleInfo.effectivePrice : (currency === 'CAD' ? (product.region_prices?.CAD || product.price) : product.price);
      
      const cartProduct = {
        ...product,
        price: effectivePrice,
        price_cad: saleInfo.isOnSale && currency === 'CAD' ? saleInfo.effectivePrice : (product.region_prices?.CAD || product.price),
        configuration: configuredPrice?.selected_options || null
      };
      
      addItem(cartProduct, quantity, { 
        selectedAddons: selectedAddonsList, 
        currency 
      });
      
      const configLabel = configuredPrice?.selected_options?.map(o => o.label).join(', ');
      const addonLabel = selectedAddonsList.map(a => `${a.name}${a.quantity > 1 ? ` x${a.quantity}` : ''}`).join(', ');
      let label = product.name;
      if (configLabel) label += ` (${configLabel})`;
      if (addonLabel) label += ` + ${addonLabel}`;
      
      toast.success(`${quantity} x ${label} added to cart`);
    }
  };

  // Quick Actions
  const handleShare = async () => {
    const shareData = {
      title: product.name,
      text: product.description?.substring(0, 100) + '...',
      url: window.location.href
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to clipboard
          navigator.clipboard.writeText(window.location.href);
          toast.success('Link copied to clipboard!');
        }
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleWishlist = () => {
    let updated;
    if (isInWishlist) {
      updated = wishlist.filter(item => item.id !== product.id);
      toast.success('Removed from wishlist');
    } else {
      updated = [...wishlist, { 
        id: product.id, 
        name: product.name, 
        price: product.price, 
        image_url: product.image_url,
        addedAt: new Date().toISOString()
      }];
      toast.success('Added to wishlist!');
    }
    setWishlist(updated);
    localStorage.setItem('wishlist', JSON.stringify(updated));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0f4f8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003456] border-t-transparent"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#f0f4f8] flex items-center justify-center">
        <p className="text-gray-500">Product not found</p>
      </div>
    );
  }

  // Combine main image with additional images
  const allImages = [product.image_url, ...(product.images || [])].filter((img, idx, arr) => 
    arr.indexOf(img) === idx // Remove duplicates
  );

  const monthlyPayment = Math.round(product.price / 24);

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />

      <div className="pt-44 pb-16 section-padding">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb and Quick Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Link to="/" className="hover:text-[#003456]">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to="/products" className="hover:text-[#003456]">Products</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-[#0a0203]">{product.name}</span>
            </div>
            
            {/* Quick Actions Bar */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="flex items-center gap-1.5 text-gray-600 hover:text-[#003456] hover:border-[#003456]"
                data-testid="quick-share-btn"
              >
                <Share2 className="h-4 w-4" />
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="flex items-center gap-1.5 text-gray-600 hover:text-[#003456] hover:border-[#003456]"
                data-testid="quick-print-btn"
              >
                <Printer className="h-4 w-4" />
                <span className="hidden sm:inline">Print</span>
              </Button>
              <Button
                variant={isInWishlist ? "default" : "outline"}
                size="sm"
                onClick={handleWishlist}
                className={`flex items-center gap-1.5 ${isInWishlist ? 'bg-red-500 hover:bg-red-600 text-white border-red-500' : 'text-gray-600 hover:text-red-500 hover:border-red-500'}`}
                data-testid="quick-wishlist-btn"
              >
                <Heart className={`h-4 w-4 ${isInWishlist ? 'fill-current' : ''}`} />
                <span className="hidden sm:inline">{isInWishlist ? 'Saved' : 'Wishlist'}</span>
              </Button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Images Gallery */}
            <div className="animate-fade-in">
              <ProductImageCarousel 
                mainImage={product.image_url} 
                mainImageDescription={product.image_description}
                images={product.images || []}
              />
            </div>

            {/* Product Info */}
            <div className="animate-slide-in-right">
              {/* Sale/Affiliate/Stock Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {calculateSalePrice(product, currency).isOnSale && (
                  <span className="text-white px-3 py-1 rounded-full text-sm font-bold" style={{ backgroundColor: '#10b981' }}>
                    SALE
                  </span>
                )}
                {product.is_affiliate_product && (
                  <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {product.affiliate_badge || 'Partner Product'}
                  </span>
                )}
                {/* Stock Status Badges */}
                {!product.in_stock && (
                  <span className="bg-gray-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                    OUT OF STOCK
                  </span>
                )}
                {product.in_stock && product.stock_quantity !== null && product.stock_quantity !== undefined && product.stock_quantity <= (product.low_stock_threshold || 5) && product.stock_quantity > 0 && (
                  <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                    LOW STOCK - Only {product.stock_quantity} left!
                  </span>
                )}
              </div>

              {/* Affiliate Notification */}
              {product.is_affiliate_product && product.affiliate_message && (
                <div className="bg-purple-50 border-l-4 border-purple-500 px-4 py-3 rounded-r-lg mb-4">
                  <p className="text-purple-800 font-medium text-sm">
                    {product.affiliate_name && <span className="font-bold">{product.affiliate_name}: </span>}
                    {product.affiliate_message}
                  </p>
                </div>
              )}

              <span className="text-sm text-[#003456] font-medium uppercase tracking-wider">
                {product.category} {product.size && `• ${product.size}`}
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-[#0a0203] mt-2 mb-4" style={{  }}>
                {product.name}
              </h1>
              
              {/* Price - with sale support */}
              <div className="mb-6">
                {(() => {
                  const saleInfo = calculateSalePrice(product, currency);
                  
                  // Map currency to region code for looking up region-specific prices
                  const regionCodeMap = { 'USD': 'US', 'CAD': 'CA', 'EUR': 'EUR' };
                  const regionCode = regionCodeMap[currency] || currency;
                  
                  // Get the region-specific price (MSRP)
                  const regionPrice = product.region_prices?.[regionCode] || product.region_prices?.[currency] || product.price;
                  
                  if (saleInfo.isOnSale && saleInfo.effectivePrice) {
                    // Product is on sale - show sale price and original price
                    const displaySalePrice = `$${saleInfo.effectivePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
                    const displayOriginalPrice = `$${saleInfo.originalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
                    const displaySavings = `$${saleInfo.savings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
                      
                    return (
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <p className="text-4xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }}>
                          {displaySalePrice}
                        </p>
                        <p className="text-xl text-gray-400 line-through" style={{ fontFamily: 'JetBrains Mono' }}>
                          {displayOriginalPrice}
                        </p>
                        <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-bold">
                          SAVE {saleInfo.savingsPercent}% ({displaySavings})
                        </span>
                      </div>
                    );
                  }
                  
                  // Product is NOT on sale - show region-specific price directly
                  return (
                    <p className="text-4xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>
                      ${regionPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                    </p>
                  );
                })()}
                {/* Financing - use region-specific price */}
                {(() => {
                  const regionCodeMap = { 'USD': 'US', 'CAD': 'CA', 'EUR': 'EUR' };
                  const regionCode = regionCodeMap[currency] || currency;
                  const saleInfo = calculateSalePrice(product, currency);
                  const effectivePrice = saleInfo.isOnSale ? saleInfo.effectivePrice : (product.region_prices?.[regionCode] || product.region_prices?.[currency] || product.price);
                  const monthlyPayment = Math.round(effectivePrice / 24);
                  return (
                    <p className="text-gray-500 mt-1">
                      or <span className="font-semibold text-[#0a0203]">${monthlyPayment}/mo</span> with as low as 0% APR financing
                    </p>
                  );
                })()}
                {/* Pricing Disclaimer */}
                <p className="text-xs text-gray-400 mt-2 italic">
                  * Pricing subject to change without notice due to market conditions.
                </p>
              </div>

              {/* Low Stock Warning */}
              {product.in_stock && product.stock_quantity !== null && product.stock_quantity !== undefined && product.stock_quantity <= LOW_STOCK_THRESHOLD && product.stock_quantity > 0 && (
                <div className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-1.5 rounded-full text-sm font-bold animate-pulse mb-6">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Only {product.stock_quantity} left - Order soon!</span>
                </div>
              )}
              
              {/* Product Description - Enhanced formatting */}
              <div className="mb-8">
                {product.description && (
                  <div className="space-y-4">
                    {/* Split description into digestible paragraphs (every ~3-4 sentences) */}
                    {(() => {
                      const sentences = product.description.split(/(?<=[.!?])\s+/);
                      const paragraphs = [];
                      for (let i = 0; i < sentences.length; i += 3) {
                        paragraphs.push(sentences.slice(i, i + 3).join(' '));
                      }
                      
                      // Show first paragraph, collapse the rest
                      if (paragraphs.length > 1) {
                        return (
                          <>
                            <p className="text-gray-600 leading-relaxed text-base">
                              {paragraphs[0]}
                            </p>
                            <details className="group mt-4">
                              <summary className="cursor-pointer bg-gradient-to-r from-[#003456] to-[#004d7a] text-white font-semibold px-6 py-3 rounded-xl hover:from-[#001d30] hover:to-[#003456] inline-flex items-center gap-2 text-base transition-all shadow-md hover:shadow-lg">
                                <span className="group-open:hidden flex items-center gap-2">
                                  <FileText className="h-4 w-4" />
                                  Read More About This Product
                                </span>
                                <span className="hidden group-open:inline flex items-center gap-2">
                                  <ChevronLeft className="h-4 w-4" />
                                  Show Less
                                </span>
                              </summary>
                              <div className="mt-4 space-y-4 p-5 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border border-blue-100 animate-fade-in">
                                {paragraphs.slice(1).map((para, idx) => (
                                  <p key={idx} className="text-gray-600 leading-relaxed text-base">
                                    {para}
                                  </p>
                                ))}
                              </div>
                            </details>
                          </>
                        );
                      }
                      return (
                        <p className="text-gray-600 leading-relaxed text-base">
                          {product.description}
                        </p>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Color/Size Options if available */}
              {(product.color || product.size) && (
                <div className="mb-6 space-y-4">
                  {product.color && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Color: </span>
                      <span className="text-sm text-[#0a0203]">{product.color}</span>
                    </div>
                  )}
                  {product.size && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Size: </span>
                      <span className="text-sm text-[#0a0203]">{product.size}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Product Configurator */}
              {product.category === 'freeze-dryer' && (
                <ProductConfigurator 
                  product={product} 
                  onConfigChange={handleConfigChange}
                />
              )}

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold text-[#0a0203] mb-4" style={{  }}>Key Features</h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {product.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-gray-600">
                        <div className="w-6 h-6 bg-[#dbeef5] rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 text-[#003456]" />
                        </div>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Product Downloads (PDF Flyers/Spec Sheets) */}
              {(product.flyer_pdf_url || product.spec_sheet_url) && (
                <div className="mb-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <h3 className="font-semibold text-[#0a0203] mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#003456]" />
                    Product Downloads
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {product.flyer_pdf_url && (
                      <a
                        href={product.flyer_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-[#003456] hover:bg-[#dbeef5]/30 transition-all text-sm font-medium text-gray-700"
                        data-testid="download-flyer-btn"
                      >
                        <Download className="h-4 w-4 text-[#003456]" />
                        Download Product Flyer
                      </a>
                    )}
                    {product.spec_sheet_url && (
                      <a
                        href={product.spec_sheet_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-[#003456] hover:bg-[#dbeef5]/30 transition-all text-sm font-medium text-gray-700"
                        data-testid="download-spec-sheet-btn"
                      >
                        <Download className="h-4 w-4 text-[#003456]" />
                        Download Spec Sheet
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Product Add-ons & Upgrades */}
              {product.addons && product.addons.length > 0 && (
                <div className="mb-8 p-4 bg-gradient-to-r from-cyan-50 to-transparent rounded-xl border border-cyan-200">
                  <h3 className="font-semibold text-[#0a0203] mb-4">Customize Your Order</h3>
                  <div className="space-y-3">
                    {/* All Add-ons/Upgrades as checkboxes with quantity */}
                    {product.addons && product.addons.length > 0 && (
                      <div className="space-y-2">
                        {product.addons.map(addon => {
                          const addonPrice = currency === 'CAD' ? (addon.price_cad || addon.price) : addon.price;
                          const isSelected = selectedAddons[addon.id]?.selected;
                          const addonQty = selectedAddons[addon.id]?.quantity || 1;
                          
                          return (
                            <div key={addon.id} className={`rounded-lg border-2 transition-all ${
                              isSelected ? 'border-[#003456] bg-[#003456]/5' : 'border-gray-200 hover:border-gray-300'
                            }`}>
                              <label className="flex items-center gap-3 p-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isSelected || false}
                                  onChange={(e) => setSelectedAddons({ 
                                    ...selectedAddons, 
                                    [addon.id]: { selected: e.target.checked, quantity: addonQty }
                                  })}
                                  className="sr-only"
                                  data-testid={`addon-checkbox-${addon.id}`}
                                />
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'border-[#003456] bg-[#003456]' : 'border-gray-300'
                                }`}>
                                  {isSelected && <Check className="h-3 w-3 text-white" />}
                                </div>
                                <div className="flex-1">
                                  <span className="font-medium">{addon.name}</span>
                                  {addon.description && <p className="text-xs text-gray-500">{addon.description}</p>}
                                </div>
                                <span className="font-bold text-green-600">+{formatPrice(addonPrice)}</span>
                              </label>
                              
                              {/* Quantity selector - always show when selected */}
                              {isSelected && (
                                <div className="flex items-center justify-between px-3 pb-3 pt-1 border-t border-gray-100">
                                  <span className="text-sm text-gray-600">Quantity:</span>
                                  <div className="flex items-center gap-2">
                                    <button 
                                      type="button"
                                      onClick={() => setSelectedAddons({
                                        ...selectedAddons,
                                        [addon.id]: { selected: true, quantity: Math.max(1, addonQty - 1) }
                                      })}
                                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50"
                                      disabled={addonQty <= 1}
                                      data-testid={`addon-qty-minus-${addon.id}`}
                                    >
                                      <Minus className="h-4 w-4" />
                                    </button>
                                    <span className="w-8 text-center font-medium">{addonQty}</span>
                                    <button 
                                      type="button"
                                      onClick={() => setSelectedAddons({
                                        ...selectedAddons,
                                        [addon.id]: { selected: true, quantity: addonQty + 1 }
                                      })}
                                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                                      data-testid={`addon-qty-plus-${addon.id}`}
                                    >
                                      <Plus className="h-4 w-4" />
                                    </button>
                                  </div>
                                  {addonQty > 1 && (
                                    <span className="text-sm font-medium text-green-600">= {formatPrice(addonPrice * addonQty)}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Live Price Summary - Updates as options are selected */}
              {product && (
                <div className="bg-gradient-to-r from-[#003456] to-[#004d7a] rounded-xl p-5 mb-6 shadow-lg" data-testid="live-price-summary">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Your Order Total
                  </h3>
                  
                  {/* Base Product Price */}
                  <div className="space-y-2 text-white">
                    {(() => {
                      const saleInfo = calculateSalePrice(product, currency);
                      
                      // Map currency to region code (admin uses US/CA, display uses USD/CAD)
                      const regionCodeMap = { 'USD': 'US', 'CAD': 'CA', 'EUR': 'EUR' };
                      const regionCode = regionCodeMap[currency] || currency;
                      
                      // Get original price (MSRP) for current currency using region code
                      const originalPrice = product.region_prices?.[regionCode] || product.region_prices?.[currency] || product.price;
                      
                      // Get effective sale price for current currency
                      const salePrice = saleInfo.isOnSale 
                        ? (product.regional_sale_prices?.[currency] || saleInfo.effectivePrice)
                        : originalPrice;
                      
                      // Calculate savings
                      const savings = originalPrice - salePrice;
                      const savingsPercent = originalPrice > 0 ? Math.round((savings / originalPrice) * 100) : 0;
                      
                      // Format price directly without currency conversion
                      const formatDirectPrice = (price) => {
                        return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
                      };
                      
                      return (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-white/80">Base Price ({product.name})</span>
                            <div className="text-right">
                              {saleInfo.isOnSale && savings > 0 && (
                                <span className="text-white/50 line-through text-sm mr-2" style={{ fontFamily: 'JetBrains Mono' }}>
                                  {formatDirectPrice(originalPrice)}
                                </span>
                              )}
                              <span className="font-medium" style={{ fontFamily: 'JetBrains Mono' }}>
                                {formatDirectPrice(salePrice)}
                              </span>
                            </div>
                          </div>
                          {saleInfo.isOnSale && savings > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-green-300">You Save</span>
                              <span className="text-green-300 font-medium" style={{ fontFamily: 'JetBrains Mono' }}>
                                -{formatDirectPrice(savings)} ({savingsPercent}% off)
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Selected Add-ons */}
                    {product.addons && product.addons.filter(a => selectedAddons[a.id]?.selected).length > 0 && (
                      <>
                        <div className="border-t border-white/20 pt-2 mt-2">
                          <span className="text-white/60 text-sm">Selected Add-ons:</span>
                        </div>
                        {product.addons.filter(a => selectedAddons[a.id]?.selected).map(addon => {
                          const addonPrice = currency === 'CAD' ? (addon.price_cad || addon.price) : addon.price;
                          const addonQty = selectedAddons[addon.id]?.quantity || 1;
                          return (
                            <div key={addon.id} className="flex justify-between items-center text-sm">
                              <span className="text-white/80">
                                + {addon.name} {addonQty > 1 ? `×${addonQty}` : ''}
                              </span>
                              <span className="text-green-300" style={{ fontFamily: 'JetBrains Mono' }}>
                                +${(addonPrice * addonQty).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    )}
                    
                    {/* Quantity Multiplier */}
                    {quantity > 1 && (
                      <div className="flex justify-between items-center text-sm border-t border-white/20 pt-2">
                        <span className="text-white/80">Quantity</span>
                        <span className="text-white" style={{ fontFamily: 'JetBrains Mono' }}>×{quantity}</span>
                      </div>
                    )}
                    
                    {/* Total */}
                    <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-[#fdc31c]">
                      <span className="text-xl font-bold text-[#fdc31c]">TOTAL</span>
                      <span className="text-2xl font-bold text-[#fdc31c]" style={{ fontFamily: 'JetBrains Mono' }}>
                        ${(calculateTotalPrice() * quantity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                      </span>
                    </div>
                  </div>
                  
                  {/* Quantity & Add to Cart - Inside the price summary */}
                  <div className="mt-6 pt-4 border-t border-white/20">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-3 bg-white rounded-full px-3 py-2 shadow-inner">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="w-10 h-10 flex items-center justify-center text-[#0a0203] hover:bg-gray-100 rounded-full transition-colors"
                          data-testid="quantity-decrease"
                        >
                          <Minus className="h-5 w-5" />
                        </button>
                        <span className="w-10 text-center font-bold text-xl" data-testid="quantity-value">{quantity}</span>
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          className="w-10 h-10 flex items-center justify-center text-[#0a0203] hover:bg-gray-100 rounded-full transition-colors"
                          data-testid="quantity-increase"
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                      </div>
                      <Button
                        onClick={handleAddToCart}
                        disabled={!product.in_stock}
                        className="flex-1 bg-[#fdc31c] hover:bg-[#e5b119] text-[#0a0203] py-7 text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 btn-scale"
                        data-testid="add-to-cart-btn"
                      >
                        <ShoppingCart className="mr-3 h-6 w-6" />
                        {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Specifications - Above Pre-Order */}
              {product.specifications && Object.keys(product.specifications).length > 0 && (
                <div className="bg-white rounded-xl p-5 mb-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-[#0a0203] mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#003456]" />
                    Specifications
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                        <span className="text-gray-600">{key}</span>
                        <span className="font-medium text-[#0a0203] text-right" style={{ fontFamily: 'JetBrains Mono' }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* What's Included Section */}
              {product.ships_with && product.ships_with.length > 0 && (
                <div className="bg-gradient-to-br from-[#dbeef5] to-white rounded-xl p-5 mb-6 border border-[#003456]/20">
                  <h3 className="text-lg font-bold text-[#0a0203] mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-[#003456]" />
                    What's Included
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {product.ships_with.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 bg-white/70 rounded-lg p-3">
                        <div className="w-8 h-8 bg-[#003456] rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-[#0a0203] font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Coming Soon / Pre-Order Notice */}
              {product.status === 'coming_soon' && (
                <div className="mb-8 p-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="h-6 w-6" />
                    <h3 className="font-bold text-xl">Coming Soon!</h3>
                  </div>
                  {product.estimated_availability && (
                    <p className="mb-4 text-blue-100">
                      Expected availability: {new Date(product.estimated_availability).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </p>
                  )}
                  <p className="mb-4 text-blue-100">Be the first to know when this product is available!</p>
                  {!notifySubmitted ? (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={nameForNotify}
                          onChange={(e) => setNameForNotify(e.target.value)}
                          placeholder="Your name"
                          className="bg-white/20 border-white/30 text-white placeholder:text-white/70 flex-1"
                          data-testid="coming-soon-name"
                        />
                        <Input
                          type="tel"
                          value={phoneForNotify}
                          onChange={(e) => setPhoneForNotify(e.target.value)}
                          placeholder="Phone (optional)"
                          className="bg-white/20 border-white/30 text-white placeholder:text-white/70 w-40"
                          data-testid="coming-soon-phone"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          value={emailForNotify}
                          onChange={(e) => setEmailForNotify(e.target.value)}
                          placeholder="Enter your email"
                          className="bg-white/20 border-white/30 text-white placeholder:text-white/70 flex-1"
                          data-testid="coming-soon-email"
                        />
                        <Button 
                          onClick={async () => {
                            if (!emailForNotify) {
                              toast.error('Please enter your email');
                              return;
                            }
                            try {
                              await axios.post(`${API_URL}/api/leads/capture`, {
                                email: emailForNotify,
                                name: nameForNotify || null,
                                phone: phoneForNotify || null,
                                lead_type: 'coming_soon',
                                product_id: product.id,
                                product_name: product.name,
                                source: window.location.href
                              });
                              setNotifySubmitted(true);
                              toast.success('You\'ll be notified when this product is available!');
                            } catch (e) {
                              toast.error('Failed to subscribe. Please try again.');
                            }
                          }}
                          className="bg-white text-blue-600 hover:bg-blue-50"
                          data-testid="coming-soon-submit"
                        >
                          <Bell className="h-4 w-4 mr-2" /> Notify Me
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-200">
                      <Check className="h-5 w-5" /> You're on the list! We'll email you when it's available.
                    </div>
                  )}
                </div>
              )}

              {product.status === 'pre_order' && (
                <div className="mb-8 p-6 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <ShoppingCart className="h-6 w-6" />
                    <h3 className="font-bold text-xl">Reserve Your Machine!</h3>
                  </div>
                  {product.estimated_availability && (
                    <p className="mb-2 text-amber-100">
                      Expected shipping: {new Date(product.estimated_availability).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </p>
                  )}
                  {product.preorder_deposit && (
                    <p className="mb-4 text-amber-100">
                      Reserve yours with a ${product.preorder_deposit} deposit - pay the rest when it ships!
                    </p>
                  )}
                  {!preorderSubmitted ? (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={nameForNotify}
                          onChange={(e) => setNameForNotify(e.target.value)}
                          placeholder="Your name *"
                          className="bg-white/20 border-white/30 text-white placeholder:text-white/70 flex-1"
                          data-testid="preorder-name"
                        />
                        <Input
                          type="tel"
                          value={phoneForNotify}
                          onChange={(e) => setPhoneForNotify(e.target.value)}
                          placeholder="Phone *"
                          className="bg-white/20 border-white/30 text-white placeholder:text-white/70 w-40"
                          data-testid="preorder-phone"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          value={emailForNotify}
                          onChange={(e) => setEmailForNotify(e.target.value)}
                          placeholder="Enter your email *"
                          className="bg-white/20 border-white/30 text-white placeholder:text-white/70 flex-1"
                          data-testid="preorder-email"
                        />
                      </div>
                      {/* City and Province/State Fields */}
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                          <Input
                            type="text"
                            value={cityForNotify}
                            onChange={(e) => setCityForNotify(e.target.value)}
                            placeholder="City"
                            className="bg-white/20 border-white/30 text-white placeholder:text-white/70 pl-10"
                            data-testid="preorder-city"
                          />
                        </div>
                        <Input
                          type="text"
                          value={provinceForNotify}
                          onChange={(e) => setProvinceForNotify(e.target.value)}
                          placeholder="Province/State"
                          className="bg-white/20 border-white/30 text-white placeholder:text-white/70 w-40"
                          data-testid="preorder-province"
                        />
                      </div>
                      <Button 
                        onClick={async () => {
                          if (!emailForNotify || !nameForNotify || !phoneForNotify) {
                            toast.error('Please fill in all required fields');
                            return;
                          }
                          try {
                            await axios.post(`${API_URL}/api/leads/capture`, {
                              email: emailForNotify,
                              name: nameForNotify,
                              phone: phoneForNotify,
                              city: cityForNotify || null,
                              province: provinceForNotify || null,
                              lead_type: 'pre_order',
                              product_id: product.id,
                              product_name: product.name,
                              source: window.location.href
                            });
                            setPreorderSubmitted(true);
                            toast.success('Pre-order request received! We\'ll contact you soon.');
                          } catch (e) {
                            toast.error('Failed to submit. Please try again.');
                          }
                        }}
                        className="w-full bg-white text-orange-600 hover:bg-orange-50 font-semibold"
                        data-testid="preorder-submit"
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" /> Reserve Now
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-200">
                      <Check className="h-5 w-5" /> Pre-order request submitted! We'll contact you soon.
                    </div>
                  )}
                </div>
              )}

              {/* Stock Alert for Out of Stock Products */}
              {!product.in_stock && (
                <div className="mb-8">
                  <StockAlert product={product} />
                </div>
              )}

              {/* Current Cart Total Banner - shows existing cart items */}
              {cartTotal > 0 && (
                <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-4 mb-4 shadow-md">
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5" />
                      <span className="font-medium">Items in Cart ({cartItemCount} {cartItemCount === 1 ? 'item' : 'items'})</span>
                    </div>
                    <div className="text-right">
                      <span className="text-green-200 text-sm">Cart Total:</span>
                      <span className="ml-2 text-xl font-bold">
                        {currency === 'CAD' ? 'CA' : ''}${cartTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Request Quote Button - PROMINENT */}
              <div className="mb-4">
                <button
                  onClick={() => setShowQuotePopup(true)}
                  className="w-full py-4 px-6 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold text-lg"
                  data-testid="request-quote-btn"
                >
                  <Mail className="h-6 w-6" />
                  <span>Have Questions? <strong>Request a Quote</strong></span>
                </button>
              </div>

              {/* Financing CTA */}
              <div className="bg-[#dbeef5] rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[#0a0203]">As Low As 0% APR Financing Available</p>
                    <p className="text-sm text-gray-600">As low as ${monthlyPayment}/mo for 24 months</p>
                  </div>
                  <Link to="/financing">
                    <Button variant="outline" size="sm" className="border-[#003456] text-[#003456]">
                      Learn More
                    </Button>
                  </Link>
                </div>
                {/* Financing Disclaimer */}
                <p className="text-xs text-gray-500 mt-3 border-t border-gray-300 pt-3">
                  <strong>Third-Party Financing Disclosure:</strong> Chinook Manufacturing does not dictate terms or conditions set by third-party financing providers. 
                  Financing is subject to credit approval and terms determined solely by the financing partner. 
                  By proceeding with financing options, you acknowledge that your information may be shared with our financing partners in accordance with applicable data and privacy policies. 
                  All financing offers are subject to change and may vary based on creditworthiness and other factors.
                </p>
              </div>

              {/* Trust Badges - Customizable */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
                <div className="text-center">
                  <Truck className="h-8 w-8 mx-auto mb-2 text-[#003456]" />
                  <p className="text-sm text-gray-600 font-medium">{product.badges?.shipping || 'Fast Shipping'}</p>
                </div>
                <div className="text-center">
                  <Shield className="h-8 w-8 mx-auto mb-2 text-[#003456]" />
                  <p className="text-sm text-gray-600 font-medium">{product.badges?.warranty || '5-Year Warranty'}</p>
                </div>
                <div className="text-center">
                  <RefreshCw className="h-8 w-8 mx-auto mb-2 text-[#003456]" />
                  <p className="text-sm text-gray-600 font-medium">{product.badges?.returns || '30-Day Returns'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping & Warranty - Two Column Layout on Desktop */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <ShippingEstimator productId={product.id} quantity={quantity} />
            <WarrantySection product={product} />
          </div>

          {/* Customer Reviews */}
          <div className="mt-16">
            <ProductReviews productId={product.id} productName={product.name} />
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-[#0a0203] mb-8" style={{  }}>
                You May Also Like
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedProducts.map((rp) => (
                  <Link key={rp.id} to={`/products/${rp.id}`} className="group">
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover">
                      <div className="aspect-square bg-gray-100 overflow-hidden relative">
                        <img src={rp.image_url} alt={rp.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        
                        {/* Badges */}
                        <div className="absolute top-3 left-3 flex flex-col gap-2">
                          {calculateSalePrice(rp, currency).isOnSale && (
                            <span className="bg-emerald-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                              SALE
                            </span>
                          )}
                          {rp.is_affiliate_product && (
                            <span className="bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                              {rp.affiliate_badge || 'Partner'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="font-semibold text-[#0a0203]">{rp.name}</h3>
                        <div className="flex items-baseline gap-2 mt-1">
                          {(() => {
                            const rpSaleInfo = calculateSalePrice(rp, currency);
                            if (rpSaleInfo.isOnSale && rpSaleInfo.effectivePrice) {
                              return (
                                <>
                                  <span className="text-lg font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }}>
                                    {formatPrice(rpSaleInfo.effectivePrice)}
                                  </span>
                                  <span className="text-sm text-gray-400 line-through">
                                    {formatPrice(rp.price)}
                                  </span>
                                </>
                              );
                            }
                            return (
                              <span className="text-lg font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>
                                {formatPrice(rp.price)}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
      
      {/* Request Quote Popup - Shows after configurable delay OR when manually triggered */}
      {product && product.status !== 'coming_soon' && (
        <RequestQuotePopup 
          product={product} 
          forceShow={showQuotePopup}
          onClose={() => setShowQuotePopup(false)}
        />
      )}
    </div>
  );
};

export default ProductDetail;
